// packages/app/src/components/dashboard/DashboardPage.tsx

import type {
  CSSProperties,
  Dispatch,
  KeyboardEvent,
  ReactNode,
  FormEvent,
  SetStateAction,
} from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, identityApiRef, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import { searchApiRef } from '@backstage/plugin-search-react';
import { parseEksTeam } from '../../utils/eksTeam';


// ─── Types ────────────────────────────────────────────────────────────────────

interface Team {
  team_key:    string;
  team_name:   string;
  description: string;
}

interface Application {
  app_key:          string;
  app_name:         string;
  team_key:         string;
  description:      string;
  env_folders:      string[];
  additional_teams: string[];
}

interface Service {
  service_key: string;
  service_name: string;
  app_key: string;
  team_key: string;
  description: string;
  service_type?: string;
  kong_enabled?: boolean;
  kong_path?: string;
  autoscaling?: boolean;
  iam_role?: boolean;
}

type ColStatus<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; items: T[] };

interface Item {
  key: string;
  name: string;

  serviceKey?: string;
  appKey?: string;
  teamKey?: string;

  subLabel?: string;
  environment?: string;
  serviceType?: string;

  kongEnabled?: boolean;
  kongPath?: string;
  autoscaling?: boolean;
  iamRole?: boolean;
}

// ─── Delete modal ─────────────────────────────────────────────────────────────

type DeleteTarget =
  | { type: 'team';        key: string; name: string; teamRepoSlug: string }
  | { type: 'application'; key: string; name: string; appRepoSlug: string }
  | { type: 'service';     key: string; name: string; appKey: string };

interface DeleteModalState {
  target:     DeleteTarget;
  loading:    boolean;
  error:      string | null;
  deleteRepo: boolean;
}

// PR result shown after a successful delete-PR request
interface PrResult {
  targetType:    'application' | 'service' | 'team';
  targetKey:     string;
  targetName:    string;
  prUrl:         string | null;
  prNumber:      number | null;
  warning?:      string;
  repoToDelete?: string | null;
  alreadyDeleted?: boolean;
  // Team deletion raises TWO PRs (okta + env0) — populated only when targetType is 'team'
  teamPrs?: {
    okta: { prUrl: string | null; status: string };
    env0: { prUrl: string | null; status: string };
  };
}

interface FeedbackEntry {
  id: number;
  user_ref: string | null;
  rating: number;
  comments: string;
  page_context: string;
  created_at: string;
}

interface PendingDeletionRequest {
  id: number;
  targetType: 'team' | 'application' | 'service';
  targetKey: string;
  targetName: string | null;
  appKey: string | null;
  requestedBy: string;
  updatedBy: string | null;
  completedBy: string | null;
  status: 'pending' | 'partially_complete' | 'completed' | 'failed';
  prStatus: Array<{
    repoSlug: string;
    branchName: string;
    prId: number | null;
    prUrl: string | null;
    status: string;
    mergedAt?: string | null;
    updatedAt?: string | null;
    reason?: string | null;
  }>;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

// NEW: Services must be matched by composite key because service_key is not globally unique.
function servicePendingKey(
  appKey: string | null | undefined,
  serviceKey: string,
): string {
  return `${appKey ?? ''}::${serviceKey}`;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractTeamKey(ref: string): string | null {
  const parsed = parseEksTeam(ref);
  return parsed ? parsed.teamKey : null;
}

// Feedback entries store the full entity ref (e.g. "user:default/bhaskar")
// — show just the username in the admin table.
function formatFeedbackUser(userRef: string | null): string {
  if (!userRef) return 'Anonymous';

  const name = userRef.slice(userRef.lastIndexOf('/') + 1);
  return name || userRef;
}

function parseArray(value: any): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    const m = value.match(/^\{(.*)\}$/);
    if (m) return m[1] ? m[1].split(',').map(s => s.trim().replace(/^"|"$/g, '')) : [];
  }
  return [];
}

// ─── Env badge colours ────────────────────────────────────────────────────────

const ENV_COLOURS: Record<string, CSSProperties> = {
  dev: { background: '#EAF3DE', color: '#3B6D11', border: '1px solid #C0DD97' },
  qa:  { background: '#E6F1FB', color: '#185FA5', border: '1px solid #B5D4F4' },
  stg: { background: '#FAEEDA', color: '#854F0B', border: '1px solid #FAC775' },
  prd: { background: '#FCEBEB', color: '#A32D2D', border: '1px solid #F7C1C1' },
};

// ─── ArgoCD ───────────────────────────────────────────────────────────────────
// One ArgoCD instance per environment. The application's app_key is used as the
// namespace filter, so clicking an env badge lands on that app's workloads.

const ARGOCD_BASE: Record<string, string> = {
  dev: 'https://argocd.dev.dtcloudhosting.com',
  qa: 'https://argocd.qa.dtcloudhosting.com',
  stg: 'https://argocd.stg.dtcloudhosting.com',
  prd: 'https://argocd.prd.dtcloudhosting.com',
};

function argocdUrl(env: string, appKey: string): string | undefined {
  const base = ARGOCD_BASE[env];
  if (!base) return undefined;
  return `${base}/applications?proj=&sync=&autoSync=&health=&namespace=${encodeURIComponent(appKey)}&cluster=&labels=&annotations=&operation=`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S: Record<string, CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#ffffff',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    overflow: 'hidden',
  },
  header: {
    padding: '12px 24px',
    background: '#ffffff',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  headerTitle: { fontSize: 20, fontWeight: 600, color: '#111111', margin: 0 },
  globalSearchWrapper: {
    position: 'relative',
    width: 320,
  },
  globalSearchInput: {
    width: '100%',
    padding: '8px 12px 8px 36px',
    borderRadius: 6,
    border: '1px solid #cccccc',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s, box-shadow 0.15s',
    background: '#ffffff',
    color: '#333333',
  },
  globalSearchIcon: {
    position: 'absolute' as const,
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#888888',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none' as const,
  },
  globalSearchClearBtn: {
    position: 'absolute' as const,
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#888888',
    cursor: 'pointer',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    borderRadius: '50%',
  },
  globalSearchDropdown: {
    position: 'absolute' as const,
    top: '100%',
    right: 0,
    width: 360,
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 1001,
    marginTop: 6,
    maxHeight: 300,
    overflowY: 'auto' as const,
  },
  globalSearchItem: {
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    cursor: 'pointer',
    transition: 'background 0.1s',
    background: 'none',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  globalSearchItemHover: {
    background: '#f5f8ff',
  },
  globalSearchItemMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  globalSearchItemType: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    color: '#1967d2',
    background: '#e8f0fe',
    padding: '2px 6px',
    borderRadius: 4,
  },
  globalSearchItemName: {
    fontSize: 13,
    fontWeight: 500,
    color: '#111111',
  },
  globalSearchItemKey: {
    fontSize: 11,
    fontFamily: "'SF Mono', monospace",
    color: '#666666',
  },
  badge: {
    fontSize: 12,
    fontWeight: 400,
    padding: '4px 12px',
    borderRadius: 20,
    background: '#e8f0fe',
    color: '#1967d2',
    border: '1px solid #c5d8fc',  
  },
  romeBadge: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 10,
    background: '#ede9fe',
    color: '#6d28d9',
    marginLeft: 8,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
},
  
  cols: { display: 'flex', flex: 1, overflow: 'hidden' },
  col: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    borderRight: '1px solid #e0e0e0',
    background: '#ffffff',
    overflow: 'hidden',
    minWidth: 0,
  },
  colLast: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    background: '#ffffff',
    overflow: 'hidden',
    minWidth: 0,
  },
  colHead: {
    padding: '10px 20px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#ffffff',
    flexShrink: 0,
  },
  colTitle: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: '#666666',
    margin: 0,
  },
  countBadge: {
    fontSize: 11,
    fontWeight: 500,
    padding: '2px 8px',
    borderRadius: 10,
    background: '#f0f0f0',
    color: '#555555',
  },
  breadcrumb: {
    padding: '6px 20px',
    fontSize: 12,
    color: '#888888',
    borderBottom: '1px solid #f0f0f0',
    background: '#fafafa',
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  bc: { color: '#1967d2', fontWeight: 500 },
  scroll: { flex: 1, overflowY: 'auto' as const, paddingBottom: 64 },
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    borderLeft: '3px solid transparent',
    borderTop: 'none',
    borderRight: 'none',
    background: 'none',
    textAlign: 'left' as const,
    outline: 'none',
    transition: 'background 0.08s',
    boxSizing: 'border-box' as const,
  },
  rowActive: { background: '#e8f0fe', borderLeftColor: '#1967d2' },
  rowHover: { background: '#f5f8ff' },
  rowPending: { background: '#fffbf0', borderLeftColor: '#f59e0b' },
  rowMain: {
    fontSize: 14,
    fontWeight: 500,
    color: '#111111',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.3',
  },
  rowMainActive: { color: '#1967d2' },
  rowMainPending: { color: '#92400e', textDecoration: 'line-through' as const },
  rowSub: {
    fontSize: 12,
    color: '#888888',
    marginTop: 2,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: "'SF Mono', 'Fira Code', monospace",
  },
  pendingBadge: {
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 7px',
    borderRadius: 10,
    background: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fcd34d',
    marginLeft: 8,
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
  chevron: {
    color: '#cccccc',
    fontSize: 16,
    marginLeft: 8,
    flexShrink: 0,
    fontWeight: 300,
  },
  chevronActive: { color: '#1967d2' },
  center: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    color: '#bbbbbb',
    fontSize: 13,
    textAlign: 'center' as const,
  },
  spinEl: {
    width: 20,
    height: 20,
    border: '2px solid #e8e8e8',
    borderTopColor: '#1967d2',
    borderRadius: '50%',
    animation: 'dashSpin 0.7s linear infinite',
    marginBottom: 10,
  },
  errTxt: { color: '#c62828', fontSize: 13 },
  initOverlay: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    flexDirection: 'column' as const,
    gap: 12,
    color: '#999999',
    fontSize: 13,
  },
  expandPanel: {
    background: '#f8fbff',
    borderLeft: '3px solid #1967d2',
    borderBottom: '1px solid #e8f0fe',
    padding: '10px 20px 12px 20px',
  },
  expandSection: { marginBottom: 8 },
  expandSectionLast: { marginBottom: 0 },
  expandLabel: {
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.07em',
    color: '#999999',
    marginBottom: 5,
    display: 'block',
  },
  badgeRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 5 },
  ownerBadge: {
    fontSize: 11,
    fontWeight: 500,
    padding: '3px 10px',
    borderRadius: 20,
    background: '#EEEDFE',
    color: '#3C3489',
    border: '1px solid #AFA9EC',
    display: 'inline-block',
  },
  addBadge: {
    fontSize: 11,
    fontWeight: 500,
    padding: '3px 10px',
    borderRadius: 20,
    background: '#f0f0f0',
    color: '#555555',
    border: '1px solid #dddddd',
    display: 'inline-block',
  },
  envBadgeBase: {
    fontSize: 11,
    fontWeight: 500,
    padding: '3px 10px',
    borderRadius: 20,
    display: 'inline-block',
  },
  emptyText: { fontSize: 11, color: '#bbbbbb', fontStyle: 'italic' },
  deleteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: 6,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
    marginLeft: 6,
    transition: 'background 0.1s, border-color 0.1s',
    color: '#cccccc',
  },
  deleteBtnHover: {
    background: '#fff1f1',
    borderColor: '#f7c1c1',
    color: '#c62828',
  },
  // Modal
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalBox: {
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: '28px 32px 24px 32px',
    minWidth: 380,
    maxWidth: 460,
    width: '100%',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#111111',
    margin: '0 0 8px 0',
  },
  modalBody: {
    fontSize: 13,
    color: '#444444',
    lineHeight: 1.6,
    margin: '0 0 20px 0',
  },
  modalHighlight: { fontWeight: 600, color: '#111111' },
  modalError: {
    fontSize: 12,
    color: '#c62828',
    background: '#fff5f5',
    border: '1px solid #f7c1c1',
    borderRadius: 6,
    padding: '8px 12px',
    marginBottom: 16,
    lineHeight: 1.5,
  },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  btnCancel: {
    padding: '7px 18px',
    borderRadius: 6,
    border: '1px solid #dddddd',
    background: '#ffffff',
    color: '#444444',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnPR: {
    padding: '7px 18px',
    borderRadius: 6,
    border: '1px solid #1967d2',
    background: '#1967d2',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
  btnSpinEl: {
    width: 12,
    height: 12,
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#ffffff',
    borderRadius: '50%',
    animation: 'dashSpin 0.7s linear infinite',
    display: 'inline-block',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  // ── Repo delete checkbox ────────────────────────────────────────────────────
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 6,
    background: '#fff8f0',
    border: '1px solid #fed7aa',
    marginBottom: 16,
    cursor: 'pointer',
  },
  checkboxInput: {
    marginTop: 2,
    width: 16,
    height: 16,
    accentColor: '#ea580c',
    cursor: 'pointer',
    flexShrink: 0,
  } as CSSProperties,
  checkboxLabel: {
    fontSize: 13,
    color: '#431407',
    lineHeight: 1.5,
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  checkboxRepoName: {
    fontFamily: "'SF Mono', 'Fira Code', monospace",
    fontSize: 12,
    color: '#9a3412',
    fontWeight: 600,
  },
  // PR success modal
  prSuccessBox: {
    background: '#ffffff',
    borderRadius: 10,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    padding: '28px 32px 24px 32px',
    minWidth: 380,
    maxWidth: 480,
    width: '100%',
  },
  prSuccessTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#166534',
    margin: '0 0 10px 0',
  },
  prSuccessBody: {
    fontSize: 13,
    color: '#444444',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  prLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    borderRadius: 6,
    background: '#1967d2',
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    marginBottom: 16,
  },
  prWarning: {
    fontSize: 12,
    color: '#92400e',
    background: '#fffbf0',
    border: '1px solid #fcd34d',
    borderRadius: 6,
    padding: '8px 12px',
    marginBottom: 16,
    lineHeight: 1.5,
  },
  prNote: {
    fontSize: 12,
    color: '#888888',
    lineHeight: 1.5,
    margin: '0 0 16px 0',
  },
  feedbackBtn: {
    padding: '10px 20px',
    borderRadius: 24,
    background: '#1967d2',
    color: '#ffffff',
    border: 'none',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 4px 16px rgba(132, 199, 252, 0.4)',
    transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
  },
  adminFeedbackBtn: {
    padding: '10px 20px',
    borderRadius: 24,
    border: '1px solid #1967d2',
    background: '#ffffff',
    color: '#1967d2',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.15s, box-shadow 0.15s, background 0.15s',
  },
  modalCard: {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    width: 440,
    padding: 32,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  starsRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 24,
  },
  starBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontSize: 32,
    outline: 'none',
    transition: 'transform 0.1s',
  },
  commentsArea: {
    width: '100%',
    height: 120,
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.15)',
    padding: 12,
    fontSize: 14,
    color: '#333333',
    background: 'rgba(255, 255, 255, 0.8)',
    resize: 'none',
    boxSizing: 'border-box',
    outline: 'none',
    marginBottom: 24,
  },
  cancelBtn: {
    padding: '10px 24px',
    borderRadius: 8,
    border: '1px solid rgba(0, 0, 0, 0.15)',
    background: '#e0e0e0',
    color: '#333333',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 24px',
    borderRadius: 8,
    border: 'none',
    background: '#1976d2',
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  successMsg: {
    color: '#2e7d32',
    fontSize: 14,
    fontWeight: 500,
    marginTop: 12,
    textAlign: 'center',
  },
  errorMsg: {
    color: '#d32f2f',
    fontSize: 14,
    fontWeight: 500,
    marginTop: 12,
    textAlign: 'center',
  },
  adminModalCard: {
    background: 'rgba(255, 255, 255, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
    width: 800,
    maxHeight: '80vh',
    padding: 32,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  adminTableContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    marginTop: 16,
    marginBottom: 20,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  adminTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    textAlign: 'left' as const,
    fontSize: 13,
  },
  th: {
    background: '#f5f5f5',
    padding: '12px 16px',
    fontWeight: 600,
    color: '#333333',
    borderBottom: '1px solid #e0e0e0',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  td: {
    padding: '12px 16px',
    color: '#555555',
    borderBottom: '1px solid #f0f0f0',
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  },
};

// ─── Utility components ───────────────────────────────────────────────────────

const SpinCSS = () => (
  <style>{`@keyframes dashSpin { to { transform: rotate(360deg) } } button { outline: none !important; border-top: none; border-right: none; }`}</style>
);
const Spin  = () => <div style={S.center}><div style={S.spinEl} /><span>Loading…</span></div>;
const Err   = ({ msg }: { msg: string }) => <div style={S.center}><span style={S.errTxt}>⚠ {msg}</span></div>;
const Empty = ({ label }: { label: string }) => <div style={S.center}><span>{label}</span></div>;

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    <path d="M2 4h12M5.333 4V2.667A.667.667 0 0 1 6 2h4a.667.667 0 0 1 .667.667V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333A.667.667 0 0 0 4.667 14h6.666a.667.667 0 0 0 .667-.667L12.667 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function DeleteButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button type="button" title="Raise deletion PR" style={{ ...S.deleteBtn, ...(hovered ? S.deleteBtnHover : {}) }} onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <TrashIcon />
    </button>
  );
}

// ─── Deletion confirmation modal ──────────────────────────────────────────────

function DeleteModal({ state, onConfirm, onCancel, onToggleRepo }: {
  state:          DeleteModalState;
  onConfirm:      () => void;
  onCancel:       () => void;
  onToggleRepo:   () => void;
}) {
  const { target, loading, error, deleteRepo } = state;
  const typeLabel = (() => {
    if (target.type === 'application') return 'application';
    if (target.type === 'service') return 'service';
    return 'team';
  })();
  const isTeam        = target.type === 'team';
  const isApplication = target.type === 'application';

  return (
    <div style={S.modalOverlay} onClick={e => e.target === e.currentTarget && onCancel()} onKeyDown={e => e.key === 'Escape' && onCancel()} role="presentation">
      <div style={S.modalBox} role="dialog" aria-modal="true">
        <p style={S.modalTitle}>{isTeam ? 'Raise deletion PRs for this team?' : `Raise deletion PR for this ${typeLabel}?`}</p>
        <p style={S.modalBody}>
          {isTeam ? (
            <>Two pull requests will be raised to remove <span style={S.modalHighlight}>"{target.name}"</span> — one in the Okta Terraform repo and one in the env0 Terraform repo. The team will be permanently deleted from the Backstage database once <strong>both PRs are approved and merged</strong>.</>
          ) : (
            <>A pull request will be raised to remove <span style={S.modalHighlight}>"{target.name}"</span> from the Terraform repo. The {typeLabel} will be permanently deleted from the Backstage database once the PR is <strong>approved and merged</strong>.</>
          )}
        </p>

        {isApplication && (
          <div style={S.checkboxRow}>
            <input
              type="checkbox"
              id="deleteRepoCheckbox"
              checked={deleteRepo}
              onChange={onToggleRepo}
              style={S.checkboxInput}
            />
            <label htmlFor="deleteRepoCheckbox" style={S.checkboxLabel}>
              Click on checkbox to delete repo:{' '}
              <span style={S.checkboxRepoName}>
                {(target as { appRepoSlug?: string }).appRepoSlug || 'tf-ws-eks-<team>-<app>'}
              </span>{' '}
              (optional)
              <br />
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                🗑 If checked, the Bitbucket repository will be permanently deleted as soon as the PR is merged.
              </span>
            </label>
          </div>
        )}

        {isTeam && (
          <div style={S.checkboxRow}>
            <input
              type="checkbox"
              id="deleteRepoCheckbox"
              checked={deleteRepo}
              onChange={onToggleRepo}
              style={S.checkboxInput}
            />
            <label htmlFor="deleteRepoCheckbox" style={S.checkboxLabel}>
              Click on checkbox to delete repo:{' '}
              <span style={S.checkboxRepoName}>
                {(target as { teamRepoSlug?: string }).teamRepoSlug || 'tf-ws-eks-<team>'}
              </span>{' '}
              (optional)
              <br />
              <span style={{ fontSize: 11, color: '#6b7280' }}>
                🗑 If checked, the Bitbucket repository will be permanently deleted once both PRs are merged.
              </span>
            </label>
          </div>
        )}

        {error && <div style={S.modalError}>{error}</div>}
        <div style={S.modalActions}>
          <button type="button" style={S.btnCancel} onClick={onCancel} disabled={loading}>Cancel</button>
          <button type="button" style={{ ...S.btnPR, ...((loading || !!error) ? S.btnDisabled : {}) }} onClick={onConfirm} disabled={loading || !!error}>
            {loading && <span style={S.btnSpinEl} />}
            {loading && 'Raising PR…'}
            {!loading && isTeam && 'Raise Deletion PRs'}
            {!loading && !isTeam && 'Raise Deletion PR'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PR success modal ─────────────────────────────────────────────────────────

function teamPrLabel(status: string): string {
  if (status === 'raised') return 'PR raised';
  if (status === 'already-open') return 'PR already open (from a previous attempt)';
  if (status === 'skipped-no-changes') return 'No changes needed — nothing to merge here';
  return 'Failed to raise PR';
}

function PrSuccessModal({ result, onClose }: { result: PrResult; onClose: () => void }) {
  const isTeam = result.targetType === 'team';

  if (result.alreadyDeleted) {
    return (
      <div style={S.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()} onKeyDown={e => e.key === 'Escape' && onClose()} role="presentation">
        <div style={S.prSuccessBox} role="dialog" aria-modal="true">
          <p style={S.prSuccessTitle}>✓ Removed — no pull request needed</p>
          <p style={S.prSuccessBody}>
            <strong>"{result.targetName}"</strong> has been removed from the dashboard.
            No matching Terraform file was found in the repository, so there was nothing
            for a pull request to remove — this usually means its onboarding PR was
            never merged in the first place.
          </p>
          <p style={S.prNote}>
            If you expected this {result.targetType} to already exist in Bitbucket,
            check whether its original onboarding pull request is still open.
          </p>
          <div style={S.modalActions}>
            <button type="button" style={S.btnCancel} onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.modalOverlay} onClick={e => e.target === e.currentTarget && onClose()} onKeyDown={e => e.key === 'Escape' && onClose()} role="presentation">
      <div style={S.prSuccessBox} role="dialog" aria-modal="true">
        <p style={S.prSuccessTitle}>{isTeam ? '✓ Deletion PRs raised' : '✓ Deletion PR raised'}</p>

        {isTeam ? (
          <p style={S.prSuccessBody}>
            Two pull requests have been processed to delete <strong>"{result.targetName}"</strong>.
            The team will be permanently removed from the Backstage database once <strong>both PRs are approved and merged</strong>.
          </p>
        ) : (
          <p style={S.prSuccessBody}>
            A pull request has been created to delete <strong>"{result.targetName}"</strong>.
            The {result.targetType} will be permanently removed from the Backstage database once the PR is <strong>approved and merged</strong>.
          </p>
        )}

        {result.warning && <div style={S.prWarning}>⚠ {result.warning}</div>}

        {isTeam && result.teamPrs && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
            <div style={{ fontSize: 13 }}>
              <strong>tf-ws-eks-okta</strong> — {teamPrLabel(result.teamPrs.okta.status)}
              {result.teamPrs.okta.prUrl && (
                <>
                  {' · '}
                  <a href={result.teamPrs.okta.prUrl} target="_blank" rel="noopener noreferrer" style={S.prLink}>
                    Open PR ↗
                  </a>
                </>
              )}
            </div>
            <div style={{ fontSize: 13 }}>
              <strong>tf-ws-eks-env0-shared-teams</strong> — {teamPrLabel(result.teamPrs.env0.status)}
              {result.teamPrs.env0.prUrl && (
                <>
                  {' · '}
                  <a href={result.teamPrs.env0.prUrl} target="_blank" rel="noopener noreferrer" style={S.prLink}>
                    Open PR ↗
                  </a>
                </>
              )}
            </div>
          </div>
        )}

        {!isTeam && result.prUrl && (
          <a href={result.prUrl} target="_blank" rel="noopener noreferrer" style={S.prLink}>
            Open PR #{result.prNumber} in Bitbucket ↗
          </a>
        )}

        {result.repoToDelete && (
          <p style={{ ...S.prNote, color: '#92400e', background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 6, padding: '8px 12px' }}>
            🗑 The Bitbucket repository <strong>{result.repoToDelete}</strong> will also be permanently deleted when this PR is merged.
          </p>
        )}

        <p style={S.prNote}>
          The item is marked as <strong>pending deletion</strong> in the list below and will disappear automatically once {isTeam ? 'both PRs are merged' : 'the PR is merged'} and processed.
        </p>

        <div style={S.modalActions}>
          <button type="button" style={S.btnCancel} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Env Badge ────────────────────────────────────────────────────────────────
// Links to the ArgoCD instance for that environment, filtered to the
// application's namespace. Falls back to a plain badge for unknown envs.

function EnvBadge({ env, appKey }: { env: string; appKey: string }) {
  const [hover, setHover] = useState(false);
  const colours = ENV_COLOURS[env] ?? { background: '#f0f0f0', color: '#555', border: '1px solid #ddd' };
  const url = argocdUrl(env, appKey);

  if (!url) {
    return <span style={{ ...S.envBadgeBase, ...colours }}>{env}</span>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${env} in ArgoCD (namespace: ${appKey})`}
      style={{
        ...S.envBadgeBase,
        ...colours,
        textDecoration: 'none',
        cursor: 'pointer',
        filter: hover ? 'brightness(0.94)' : undefined,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={e => e.stopPropagation()}
    >
      {env}
    </a>
  );
}

// ─── App Detail Panel ─────────────────────────────────────────────────────────

function AppDetailPanel({ app }: { app: Application }) {
  const envFolders      = parseArray(app.env_folders);
  const additionalTeams = parseArray(app.additional_teams);
  return (
    <div style={S.expandPanel}>
      <div style={S.expandSection}>
        <span style={S.expandLabel}>Owner Team</span>
        <div style={S.badgeRow}><span style={S.ownerBadge}>{app.team_key}</span></div>
      </div>
      <div style={S.expandSection}>
        <span style={S.expandLabel}>Additional Teams</span>
        <div style={S.badgeRow}>
          {additionalTeams.length > 0 ? additionalTeams.map(t => <span key={t} style={S.addBadge}>{t}</span>) : <span style={S.emptyText}>none</span>}
        </div>
      </div>
      <div style={S.expandSectionLast}>
        <span style={S.expandLabel}>Environments</span>
        <div style={S.badgeRow}>
          {envFolders.length > 0
            ? envFolders.map(e => <EnvBadge key={e} env={e} appKey={app.app_key} />)
            : <span style={S.emptyText}>not configured</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Row style helpers (avoids nested ternaries flagged by eslint) ────────────

function rowStateStyle(active: boolean, pending: boolean, hover: boolean): CSSProperties {
  if (active)  return S.rowActive;
  if (pending) return S.rowPending;
  if (hover)   return S.rowHover;
  return {};
}

function rowMainStyle(active: boolean, pending: boolean): CSSProperties {
  if (active)  return S.rowMainActive;
  if (pending) return S.rowMainPending;
  return {};
}

// The badge used to be a plain <span> with no way to reach the PR it refers
// to. When a URL is known, render an actual link instead — stopPropagation
// so it doesn't also trigger the row's onClick (which selects the item).
function PendingBadge({ url }: { url?: string }) {
  if (!url) {
    return <span style={S.pendingBadge}>PR pending</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ ...S.pendingBadge, textDecoration: 'none', cursor: 'pointer' }}
      onClick={e => e.stopPropagation()}
      title="Open the pending pull request"
    >
      PR pending ↗
    </a>
  );
}

// ─── Service detail panel ──────────────────────────────────────────────────────

const ENVS = ['dev', 'qa', 'stg', 'prd'] as const;

function ServiceDetailPanel({ serviceKey, serviceName, appKey }: { serviceKey: string; serviceName: string; appKey: string }) {
  const kubernetesApi = useApi(kubernetesApiRef);
  const navigate = useNavigate();
  const [versions, setVersions] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const path = `/apis/apps/v1/namespaces/${appKey}/deployments/${serviceKey}`;
    const tryProxy = async (clusterName: string) => {
      const resp = await (kubernetesApi as any).proxy({ clusterName, path });
      const data = await resp.json();
      const image: string = data?.spec?.template?.spec?.containers?.[0]?.image ?? '';
      return image.includes(':') ? image.split(':').pop() ?? null : null;
    };

    Promise.all(
      ENVS.map(async env => {
        try {
          const tag = await tryProxy(`dt-shared-${env}`).catch(() =>
            env === 'dev' ? tryProxy('docker-desktop') : Promise.resolve(null),
          );
          return [env, tag] as [string, string | null];
        } catch {
          return [env, null] as [string, string | null];
        }
      }),
    ).then(results => {
      setVersions(Object.fromEntries(results));
      setLoading(false);
    });
  }, [serviceKey, appKey, kubernetesApi]);

  return (
    <div
      style={{
        padding: '6px 12px 10px 16px',
        borderTop: '1px solid #eee',
        background: '#f9fafb',
      }}
    >
      {loading ? (
        <span style={{ fontSize: 11, color: '#888' }}>Loading versions…</span>
      ) : (
        <div
          style={{
            display: 'flex',
            gap: 5,
            flexWrap: 'wrap' as const,
            alignItems: 'center',
          }}
        >
          {ENVS.map(env => {
            const tag = versions[env];
            if (!tag) return null;
            return (
              <span
                key={env}
                style={{
                  fontSize: 11,
                  padding: '2px 7px',
                  borderRadius: 10,
                  fontFamily: 'monospace',
                  ...ENV_COLOURS[env],
                }}
              >
                {env}: {tag}
              </span>
            );
          })}
          {ENVS.every(e => !versions[e]) && (
            <span style={{ fontSize: 11, color: '#aaa' }}>
              No deployments found
            </span>
          )}
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              navigate(`/catalog/default/component/${serviceKey}`);
            }}
            style={{
              fontSize: 11,
              padding: '2px 9px',
              borderRadius: 10,
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              marginLeft: 4,
            }}
          >
            K8s →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Column ───────────────────────────────────────────────────────────────────

// One column of the teams → applications → services drill-down. Row markup
// lives only here; per-column differences are passed in as props.
function Column({
  title,
  state,
  selected,
  onSelect,
  onDelete,
  canDelete,
  pendingKeys,
  pendingUrls,
  breadcrumb,
  idleLabel,
  emptyLabel,
  isLast,
  showChevron = true,
  getPendingKey = key => key,
  renderDetail,
}: {
  title: string;
  state: ColStatus<Item>;
  selected: string | null;
  onSelect: (k: string) => void;
  onDelete?: (key: string, name: string) => void;
  canDelete?: boolean;
  pendingKeys?: Set<string>;
  pendingUrls?: Map<string, string>;
  breadcrumb?: ReactNode;
  idleLabel: string;
  emptyLabel: string;
  isLast?: boolean;
  /** Show the › chevron on each row. */
  showChevron?: boolean;
  /** Maps a row key to its key in pendingKeys/pendingUrls. */
  getPendingKey?: (key: string) => string;
  /** Rendered under the active row. */
  renderDetail?: (item: Item) => ReactNode;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const items = state.status === 'ready' ? state.items : [];
  const pending = pendingKeys ?? new Set<string>();

  return (
    <div style={isLast ? S.colLast : S.col}>
      <div style={S.colHead}>
        <span style={S.colTitle}>{title}</span>
        {state.status === 'ready' && (
          <span style={S.countBadge}>{items.length}</span>
        )}
      </div>
      {breadcrumb && <div style={S.breadcrumb}>{breadcrumb}</div>}
      {state.status === 'loading' && <Spin />}
      {state.status === 'error' && <Err msg={state.message} />}
      {state.status === 'idle' && <Empty label={idleLabel} />}
      {state.status === 'ready' && items.length === 0 && (
        <Empty label={emptyLabel} />
      )}
      {state.status === 'ready' && items.length > 0 && (
        <div style={S.scroll}>
          {items.map(item => {
            const active = selected === item.key;
            const hover = hovered === item.key && !active;
            const pendingKey = getPendingKey(item.key);
            const isPend = pending.has(pendingKey);

            return (
              <div key={item.key}>
                <div
                  style={{ ...S.row, ...rowStateStyle(active, isPend, hover) }}
                  onMouseEnter={() => setHovered(item.key)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <button
                    type="button"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      padding: 0,
                      textAlign: 'left',
                      cursor: 'pointer',
                      font: 'inherit',
                    }}
                    onClick={() => onSelect(item.key)}
                    aria-expanded={active}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          ...S.rowMain,
                          ...rowMainStyle(active, isPend),
                        }}
                      >
                        {item.name}
                      </div>
                      {item.subLabel && item.subLabel !== item.name && (
                        <div style={S.rowSub}>{item.subLabel}</div>
                      )}
                    </div>
                    {isPend && (
                      <PendingBadge url={pendingUrls?.get(pendingKey)} />
                    )}
                    {showChevron && (
                      <span
                        style={{
                          ...S.chevron,
                          ...(active ? S.chevronActive : {}),
                        }}
                      >
                        ›
                      </span>
                    )}
                  </button>

                  {canDelete && onDelete && !isPend && (
                    <DeleteButton
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(item.key, item.name);
                      }}
                    />
                  )}
                </div>
                {active && renderDetail?.(item)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Audit Log Tab Component ──────────────────────────────────────────────────

function formatActor(actor: string | null | undefined): string {
  if (!actor) return 'unknown';
  let cleaned = actor.trim();
  if (cleaned.startsWith('user:default/')) {
    cleaned = cleaned.replace('user:default/', '');
  } else if (cleaned.startsWith('user:')) {
    cleaned = cleaned.replace('user:', '');
  }
  if (cleaned.includes('@')) {
    cleaned = cleaned.split('@')[0];
  }
  return cleaned || 'unknown';
}

function AuditLogView({
  logs,
  total,
  loading,
  error,
  entityType,
  setEntityType,
  entityKey,
  setEntityKey,
  actor,
  setActor,
  action,
  setAction,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  page,
  setPage,
  limit,
  onRefresh,
  onSelectRecord,
  backendFetch,
}: {
  logs: any[];
  total: number;
  loading: boolean;
  error: string | null;
  entityType: string;
  setEntityType: (v: string) => void;
  entityKey: string;
  setEntityKey: (v: string) => void;
  actor: string;
  setActor: (v: string) => void;
  action: string;
  setAction: (v: string) => void;
  startDate: string;
  setStartDate: (v: string) => void;
  endDate: string;
  setEndDate: (v: string) => void;
  page: number;
  setPage: (p: number | ((prev: number) => number)) => void;
  limit: number;
  onRefresh: () => void;
  onSelectRecord: (rec: any) => void;
  backendFetch: (pluginId: string, path: string, init?: RequestInit) => Promise<Response>;
}) {
  const totalPages = Math.ceil(total / limit) || 1;
  const [isExporting, setIsExporting] = useState(false);

  const handleReset = () => {
    setEntityType('all');
    setEntityKey('');
    setActor('');
    setAction('all');
    setStartDate('');
    setEndDate('');
    setPage(0);
  };

  const fetchExportData = async (): Promise<any[]> => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      if (entityType && entityType !== 'all') params.append('entityType', entityType);
      if (entityKey.trim()) params.append('entityKey', entityKey.trim());
      if (actor.trim()) params.append('actor', actor.trim());
      if (action && action !== 'all') params.append('action', action);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      params.append('limit', '5000');
      params.append('offset', '0');

      const res = await backendFetch('audit-log', `?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        return data.logs || [];
      }
    } catch {
      // fallback to current logs
    } finally {
      setIsExporting(false);
    }
    return logs;
  };

  const handleExportCSV = async () => {
    const data = await fetchExportData();
    if (!data.length) return;

    const headers = [
      'ID',
      'Timestamp',
      'Entity Type',
      'Entity Key',
      'Action',
      'Performed By',
      'Status',
      'Error',
      'Metadata',
    ];

    const escapeCsv = (val: any): string => {
      if (val === null || val === undefined) return '""';
      const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const rows = data.map(item => [
      item.id,
      new Date(item.timestamp).toISOString(),
      item.entity_type,
      item.entity_key,
      item.action,
      formatActor(item.actor),
      item.status,
      item.error ?? '',
      item.metadata ?? {},
    ].map(escapeCsv).join(','));

    const csvContent = [headers.map(escapeCsv).join(','), ...rows].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-log-report-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const statusStyle = (status: string): CSSProperties => {
    const st = (status || '').toLowerCase();
    if (st === 'success' || st === 'completed') {
      return { background: '#e6f4ea', color: '#137333', border: '1px solid #ceebe1' };
    }
    if (st === 'failure' || st === 'failed') {
      return { background: '#fce8e6', color: '#c5221f', border: '1px solid #f9d2ce' };
    }
    return { background: '#e8f0fe', color: '#1967d2', border: '1px solid #c5d8fc' };
  };

  const actionBadgeStyle = (act: string): CSSProperties => {
    const a = (act || '').toLowerCase();
    if (a.includes('create')) return { background: '#e8f5e9', color: '#2e7d32' };
    if (a.includes('delete')) return { background: '#ffebee', color: '#c62828' };
    if (a.includes('pr')) return { background: '#fff3e0', color: '#e65100' };
    return { background: '#f5f5f5', color: '#424242' };
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 24, background: '#fafafa' }}>
      {/* Filters Bar */}
      <div style={{ background: '#ffffff', borderRadius: 8, padding: '16px 20px', border: '1px solid #e0e0e0', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#111111' }}>
            Audit Log Query & Filter
          </h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isExporting || total === 0}
              style={{
                padding: '6px 12px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #137333',
                background: '#ffffff',
                color: '#137333',
                fontWeight: 500,
                cursor: isExporting || total === 0 ? 'not-allowed' : 'pointer',
                opacity: isExporting || total === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
              title="Download filtered report as CSV"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download CSV
            </button>
            <button
              type="button"
              onClick={handleReset}
              style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid #cccccc', background: '#ffffff', color: '#555555', cursor: 'pointer' }}
            >
              Reset Filters
            </button>
            <button
              type="button"
              onClick={onRefresh}
              style={{ padding: '6px 12px', fontSize: 12, borderRadius: 6, border: 'none', background: '#1967d2', color: '#ffffff', fontWeight: 500, cursor: 'pointer' }}
            >
              Refresh
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
          <div>
            <label htmlFor="audit-filter-entity-type" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4, textTransform: 'uppercase' }}>Entity Type</label>
            <select
              id="audit-filter-entity-type"
              value={entityType}
              onChange={e => { setEntityType(e.target.value); setPage(0); }}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #cccccc', fontSize: 13, background: '#ffffff' }}
            >
              <option value="all">All Types</option>
              <option value="team">Team</option>
              <option value="application">Application</option>
              <option value="service">Service</option>
              <option value="scaffolder">Scaffolder</option>
            </select>
          </div>

          <div>
            <label htmlFor="audit-filter-action" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4, textTransform: 'uppercase' }}>Action</label>
            <select
              id="audit-filter-action"
              value={action}
              onChange={e => { setAction(e.target.value); setPage(0); }}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #cccccc', fontSize: 13, background: '#ffffff' }}
            >
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="delete">Delete</option>
              <option value="pr_raised">PR Raised</option>
              <option value="pr_merged">PR Merged</option>
              <option value="scaffold_started">Scaffold Started</option>
              <option value="scaffold_failed">Scaffold Failed</option>
            </select>
          </div>

          <div>
            <label htmlFor="audit-filter-entity-key" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4, textTransform: 'uppercase' }}>Entity Key</label>
            <input
              id="audit-filter-entity-key"
              type="text"
              placeholder="e.g. plat-team"
              value={entityKey}
              onChange={e => { setEntityKey(e.target.value); setPage(0); }}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #cccccc', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label htmlFor="audit-filter-actor" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4, textTransform: 'uppercase' }}>Performed By</label>
            <input
              id="audit-filter-actor"
              type="text"
              placeholder="e.g. username"
              value={actor}
              onChange={e => { setActor(e.target.value); setPage(0); }}
              style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #cccccc', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label htmlFor="audit-filter-start-date" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4, textTransform: 'uppercase' }}>Start Date</label>
            <input
              id="audit-filter-start-date"
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setPage(0); }}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cccccc', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label htmlFor="audit-filter-end-date" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#666666', marginBottom: 4, textTransform: 'uppercase' }}>End Date</label>
            <input
              id="audit-filter-end-date"
              type="date"
              value={endDate}
              onChange={e => { setEndDate(e.target.value); setPage(0); }}
              style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cccccc', fontSize: 13, boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

      {/* Audit Log Data Table */}
      <div style={{ flex: 1, background: '#ffffff', borderRadius: 8, border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {(() => {
            if (loading) return <Spin />;
            if (error) return <Err msg={error} />;
            if (logs.length === 0) return <Empty label="No audit log entries matching filters" />;

            return (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555' }}>Timestamp</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555' }}>Entity Type</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555' }}>Entity Key</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555' }}>Action</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555' }}>Performed By</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555' }}>Status</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, color: '#555555', textAlign: 'right' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: any) => (
                    <tr key={log.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 16px', color: '#666666', fontFamily: 'monospace', fontSize: 12 }}>
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: '#f0f0f0', color: '#333333', textTransform: 'uppercase' }}>
                          {log.entity_type}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 500, color: '#111111' }}>
                        {log.entity_key}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, ...actionBadgeStyle(log.action), textTransform: 'uppercase' }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#444444' }}>
                        {formatActor(log.actor)}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 12, ...statusStyle(log.status) }}>
                          {log.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => onSelectRecord(log)}
                          style={{ padding: '4px 10px', fontSize: 12, borderRadius: 4, border: '1px solid #1967d2', background: '#ffffff', color: '#1967d2', cursor: 'pointer', fontWeight: 500 }}
                        >
                          Inspect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </div>

        {/* Pagination Footer */}
        <div style={{ padding: '12px 340px 12px 20px', borderTop: '1px solid #e0e0e0', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: '#666666' }}>
          <div>
            Showing {total === 0 ? 0 : page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} entries
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              disabled={page === 0 || loading}
              onClick={() => setPage(p => Math.max(0, p - 1))}
              style={{ padding: '5px 12px', fontSize: 12, borderRadius: 4, border: '1px solid #cccccc', background: '#ffffff', cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}
            >
              Previous
            </button>
            <span>Page {page + 1} of {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages - 1 || loading}
              onClick={() => setPage(p => p + 1)}
              style={{ padding: '5px 12px', fontSize: 12, borderRadius: 4, border: '1px solid #cccccc', background: '#ffffff', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: page >= totalPages - 1 ? 0.5 : 1 }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DashboardPage ────────────────────────────────────────────────────────────

type DashboardIdentity = {
  isPlatLead: boolean;
  isAnyLead: boolean;
  isAnyDev: boolean;
  teamKeys: Set<string>;
};

/**
 * Collapse a user's ownership entity refs into the role flags the dashboard
 * and audit-log pages need. Shared by both pages' identity effects so the
 * eks-team parsing lives in exactly one place (S3776).
 */
function resolveDashboardIdentity(
  ownershipEntityRefs: string[] | undefined,
): DashboardIdentity {
  let isPlatLead = false;
  let isAnyLead = false;
  let isAnyDev = false;
  const teamKeys = new Set<string>();

  for (const ref of ownershipEntityRefs ?? []) {
    const parsed = parseEksTeam(ref);
    if (!parsed) continue;

    teamKeys.add(parsed.teamKey);
    if (parsed.role === 'lead') {
      isAnyLead = true;
      if (parsed.isPlatform) isPlatLead = true;
    } else if (parsed.role === 'dev') {
      isAnyDev = true;
    }
  }

  return { isPlatLead, isAnyLead, isAnyDev, teamKeys };
}

function roleBadge(identity: DashboardIdentity): string {
  if (identity.isPlatLead) {
    return 'eks-team-plat-lead · sees all teams, apps, services';
  }
  if (identity.teamKeys.size > 0) {
    return `sees ${[...identity.teamKeys].join(' + ')} only`;
  }
  return 'no team membership detected';
}

function appsBreadcrumb(
  identity: DashboardIdentity,
  selTeam: Item | null,
): ReactNode {
  if (identity.isPlatLead) {
    return selTeam ? (
      <>
        <span style={S.bc}>{selTeam.name}</span>
        {' › Applications'}
      </>
    ) : undefined;
  }
  if (identity.teamKeys.size > 0) {
    return (
      <>
        {'My Team Applications — '}
        <span style={S.bc}>{[...identity.teamKeys].join(', ')}</span>
      </>
    );
  }
  return undefined;
}

function servicesBreadcrumb(
  selTeam: Item | null,
  selApp: Item | null,
): ReactNode {
  if (!selApp) return undefined;
  return (
    <>
      {selTeam && (
        <>
          <span style={S.bc}>{selTeam.name}</span>
          {' › '}
        </>
      )}
      <span style={S.bc}>{selApp.name}</span>
      {' › Services'}
    </>
  );
}

type SetCol = Dispatch<SetStateAction<ColStatus<Item>>>;

/**
 * Resolve the { pluginId, path } pair for a delete request. Kept separate from
 * confirmDelete so the per-target-type branching doesn't inflate it (S3776).
 * backendFetch needs pluginId split from path so it can resolve the plugin's
 * real backend base URL first.
 */
function resolveDeleteEndpoint(
  target: DeleteTarget,
  deleteRepo: boolean,
): { pluginId: string; path: string } {
  if (target.type === 'service') {
    // app_key is REQUIRED by the backend — service_key alone is not unique
    // (composite PK), so the request must always include it.
    const params = new URLSearchParams({ app_key: target.appKey });
    return { pluginId: 'services', path: `/${target.key}?${params.toString()}` };
  }

  const pluginId = target.type === 'team' ? 'teams' : 'applications';
  const query = deleteRepo
    ? `?${new URLSearchParams({ deleteRepo: 'true' }).toString()}`
    : '';
  return { pluginId, path: `/${target.key}${query}` };
}

/**
 * Drop the item with the given key from a "ready" column, leaving idle/loading/
 * error columns untouched. Used wherever a delete or a poll removes a row, so
 * the `status === 'ready' ? filter : prev` dance isn't repeated inline (S3776,
 * S2004).
 */
function removeColItem(setCol: SetCol, key: string): void {
  setCol(prev =>
    prev.status === 'ready'
      ? { ...prev, items: prev.items.filter(i => i.key !== key) }
      : prev,
  );
}

/**
 * For each pending key that is no longer present in `liveKeys` (the webhook has
 * finished deleting it server-side), drop it from its column and its pending
 * set and run `onRemoved`. Pulled out of the polling effect so the effect stops
 * nesting `forEach → if → setState(prev => …)` five deep (S2004).
 */
function reconcilePending(opts: {
  pendingKeys: Set<string>;
  liveKeys: Set<string>;
  setCol: SetCol;
  setPending: Dispatch<SetStateAction<Set<string>>>;
  onRemoved?: (key: string) => void;
}): void {
  const { pendingKeys, liveKeys, setCol, setPending, onRemoved } = opts;
  pendingKeys.forEach(key => {
    if (liveKeys.has(key)) return;
    removeColItem(setCol, key);
    setPending(prev => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    onRemoved?.(key);
  });
}

export const DashboardPage = () => {
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const searchApi = useApi(searchApiRef);
  const navigate = useNavigate();

  // Resolves a relative path (e.g. '/teams') against the REAL backend URL
  // for a given plugin ID (e.g. 'teams' -> https://api.tools.dtcloudhosting.com/api/teams),
  // instead of resolving against the frontend's own origin. This is what
  // makes API calls work correctly now that frontend (Nginx) and backend
  // (Node.js) are separate services on separate origins/hosts.
  //
  // Also explicitly attaches the user's identity token as an Authorization
  // header. Backstage's default fetchApi only auto-attaches this header for
  // requests it resolved itself via discoveryApi — a plain relative fetch
  // (e.g. fetchApi.fetch('/api/teams')) never goes through that resolution,
  // so once frontend and backend are on different origins the request would
  // otherwise reach the backend with no credentials and get a 401.
  const backendFetch = useCallback(
    async (pluginId: string, path: string, init?: RequestInit) => {
      const baseUrl = await discoveryApi.getBaseUrl(pluginId);
      const { token } = await identityApi.getCredentials();
      return fetchApi.fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
    [discoveryApi, fetchApi, identityApi],
  );

  // ── Identity ───────────────────────────────────────────────────────────────
  const [identity, setIdentity] = useState<{
    isPlatLead: boolean;
    isAnyLead: boolean;
    isAnyDev: boolean;
    teamKeys: Set<string>;
  } | null>(null);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(id => {
      setIdentity(resolveDashboardIdentity(id.ownershipEntityRefs));
    });
  }, [identityApi]);

  // ── Data state ─────────────────────────────────────────────────────────────
  const [allApps, setAllApps] = useState<Application[] | null>(null);
  const [allServices, setAllServices] = useState<Service[] | null>(null);

  const [teamsCol, setTeamsCol] = useState<ColStatus<Item>>({ status: 'idle' });
  const [appsCol, setAppsCol] = useState<ColStatus<Item>>({ status: 'idle' });
  const [servicesCol, setServicesCol] = useState<ColStatus<Item>>({
    status: 'idle',
  });

  const [selTeam, setSelTeam] = useState<Item | null>(null);
  const [selApp, setSelApp] = useState<Item | null>(null);
  const [selService, setSelService] = useState<string | null>(null);

  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [globalSearchFocused, setGlobalSearchFocused] = useState(false);
  const [hoveredSearchIndex, setHoveredSearchIndex] = useState<number | null>(
    null,
  );
  const [matchedSearchResults, setMatchedSearchResults] = useState<
    Array<{
      key: string;
      name: string;
      type: string;
    }>
  >([]);
  const [isSearching, setIsSearching] = useState(false);

  // ── Pending deletion keys (PR raised, waiting for webhook after merge) ──────
  const [pendingApps, setPendingApps] = useState<Set<string>>(new Set());
  const [pendingServices, setPendingServices] = useState<Set<string>>(
    new Set(),
  );
  // Teams: dual-PR flow (okta + env0) — both must merge before the webhook
  // deletes the DB row. Same "pending until poll detects removal" pattern.
  const [pendingTeams, setPendingTeams] = useState<Set<string>>(new Set());
  // Keyed the same as pendingApps/pendingServices/pendingTeams (service keys
  // use servicePendingKey). Lets the "PR pending" badge link somewhere.
  const [pendingUrls, setPendingUrls] = useState<Map<string, string>>(new Map());

  const [pendingDeletionRequests, setPendingDeletionRequests] = useState<
    PendingDeletionRequest[]
  >([]);

  // NEW: Fetch persisted pending deletion state from the backend.
  const fetchPendingDeletionRequests = useCallback(async () => {
    const response = await backendFetch('deletion-requests', '/pending', {
      headers: { 'Cache-Control': 'no-cache' },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: { deletionRequests: PendingDeletionRequest[] } =
      await response.json();

    const requests = data.deletionRequests ?? [];

    // NEW: Build pending app/team/service sets from durable backend state.
    setPendingDeletionRequests(requests);
    setPendingTeams(
      new Set(
        requests
          .filter(request => request.targetType === 'team')
          .map(request => request.targetKey),
      ),
    );
    setPendingApps(
      new Set(
        requests
          .filter(request => request.targetType === 'application')
          .map(request => request.targetKey),
      ),
    );
    setPendingServices(
      new Set(
        requests
          .filter(
            request =>
              request.targetType === 'service' && Boolean(request.appKey),
          )
          .map(request => servicePendingKey(request.appKey, request.targetKey)),
      ),
    );

    // The "PR pending" badge only ever showed a Set membership check — the
    // actual PR link was sitting unused in request.prStatus the whole time.
    // Pick the first still-open PR (fall back to the first entry with any
    // URL) so the badge has somewhere to send someone who clicks it.
    const urlEntries = requests.map(request => {
      const key =
        request.targetType === 'service'
          ? servicePendingKey(request.appKey, request.targetKey)
          : request.targetKey;
      const pr =
        request.prStatus.find(p => p.prUrl && p.status !== 'merged') ??
        request.prStatus.find(p => p.prUrl);
      return [key, pr?.prUrl ?? null] as const;
    });
    setPendingUrls(
      new Map(
        urlEntries.filter((entry): entry is [string, string] => entry[1] !== null),
      ),
    );
  }, [backendFetch]);

  // NEW: Load persisted pending state once the dashboard has identity.
  useEffect(() => {
    if (!identity) {
      return;
    }

    fetchPendingDeletionRequests().catch(() => {
      // NEW: Pending state fetch errors are intentionally non-blocking for dashboard load.
    });
  }, [identity, fetchPendingDeletionRequests]);

  // NEW: Refresh persisted pending deletion state periodically.
  useEffect(() => {
    if (!identity) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      fetchPendingDeletionRequests().catch(() => {
        // NEW: Silent retry on next interval.
      });
    }, 10_000);

    return () => window.clearInterval(interval);
  }, [identity, fetchPendingDeletionRequests]);

  // ── Modals ─────────────────────────────────────────────────────────────────
  const [deleteModal, setDeleteModal] = useState<DeleteModalState | null>(null);
  const [prResult, setPrResult] = useState<PrResult | null>(null);

  // ── Feedback states ────────────────────────────────────────────────────────
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [identifyMe, setIdentifyMe] = useState(false);
  const [isFeedbackBtnHovered, setIsFeedbackBtnHovered] = useState(false);

  const [isAdminFeedbackOpen, setIsAdminFeedbackOpen] = useState(false);
  const [adminFeedbackList, setAdminFeedbackList] = useState<FeedbackEntry[]>(
    [],
  );
  const [isLoadingAdminFeedback, setIsLoadingAdminFeedback] = useState(false);
  const [adminFeedbackError, setAdminFeedbackError] = useState<string | null>(
    null,
  );
  const [isAdminFeedbackBtnHovered, setIsAdminFeedbackBtnHovered] =
    useState(false);

  const handleFeedbackSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (feedbackRating === 0) {
        setFeedbackError('Please select a star rating');
        return;
      }
      setIsSubmittingFeedback(true);
      setFeedbackError(null);
      try {
        const r = await backendFetch('feedback', '', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rating: feedbackRating,
            comments: feedbackComments,
            pageContext: 'dashboard',
            identifyMe,
          }),
        });
        if (!r.ok) {
          throw new Error(`HTTP ${r.status}: ${r.statusText}`);
        }
        setFeedbackSuccess(true);
        setFeedbackComments('');
        setFeedbackRating(0);
        setIdentifyMe(false);
        setTimeout(() => {
          setIsFeedbackOpen(false);
          setFeedbackSuccess(false);
        }, 1500);
      } catch (err: any) {
        setFeedbackError(err.message || 'Failed to submit feedback');
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
    [backendFetch, feedbackRating, feedbackComments, identifyMe],
  );

  const fetchAdminFeedback = useCallback(async () => {
    setIsLoadingAdminFeedback(true);
    setAdminFeedbackError(null);
    try {
      const r = await backendFetch('feedback', '');
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}: ${r.statusText}`);
      }
      const data = await r.json();
      setAdminFeedbackList(data.feedback || []);
    } catch (err: any) {
      setAdminFeedbackError(err.message || 'Failed to load feedback logs');
    } finally {
      setIsLoadingAdminFeedback(false);
    }
  }, [backendFetch]);

  useEffect(() => {
    if (isAdminFeedbackOpen) {
      fetchAdminFeedback();
    }
  }, [isAdminFeedbackOpen, fetchAdminFeedback]);

  const closeFeedbackModal = useCallback(() => {
    setIsFeedbackOpen(false);
    setFeedbackRating(0);
    setFeedbackComments('');
    setFeedbackError(null);
  }, []);

  const renderAdminFeedbackContent = () => {
    if (isLoadingAdminFeedback) {
      return <Spin />;
    }
    if (adminFeedbackError) {
      return <Err msg={adminFeedbackError} />;
    }
    if (adminFeedbackList.length === 0) {
      return (
        <div style={{ ...S.center, color: '#666', fontSize: 14 }}>
          No feedback entries found.
        </div>
      );
    }
    return (
      <div style={S.adminTableContainer}>
        <table style={S.adminTable}>
          <thead>
            <tr>
              <th style={S.th}>Date</th>
              <th style={S.th}>User</th>
              <th style={S.th}>Rating</th>
              <th style={S.th}>Comments</th>
              <th style={S.th}>Context</th>
            </tr>
          </thead>
          <tbody>
            {adminFeedbackList.map(entry => (
              <tr key={entry.id}>
                <td style={S.td}>
                  {new Date(entry.created_at).toLocaleString()}
                </td>
                <td style={S.td}>
                  {formatFeedbackUser(entry.user_ref)}
                </td>
                <td style={S.td}>
                  <div style={{ display: 'flex', gap: 2, fontSize: 16 }}>
                    {[1, 2, 3, 4, 5].map(s => (
                      <span
                        key={s}
                        style={{
                          color: s <= entry.rating ? '#FFB300' : '#cccccc',
                        }}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                </td>
                <td style={S.td}>
                  {entry.comments || <em style={{ color: '#bbb' }}>none</em>}
                </td>
                <td style={S.td}>{entry.page_context}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const getApps = useCallback(async (): Promise<Application[]> => {
    if (allApps) return allApps;
    const r = await backendFetch('applications', '');
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    const data: { applications: Application[] } = await r.json();
    setAllApps(data.applications);
    return data.applications;
  }, [backendFetch, allApps]);

  const getServices = useCallback(async (): Promise<Service[]> => {
    if (allServices) return allServices;
    const r = await backendFetch('services', '');
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    const data: { services: Service[] } = await r.json();
    setAllServices(data.services);
    return data.services;
  }, [backendFetch, allServices]);

  const loadTeams = useCallback(async () => {
    setTeamsCol({ status: 'loading' });
    try {
      const r = await backendFetch('teams', '');
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${r.statusText}`);
      const data: { teams: Team[] } = await r.json();
      setTeamsCol({
        status: 'ready',
        items: data.teams
          .sort((a, b) => a.team_name.localeCompare(b.team_name))
          .map(t => ({
            key: t.team_key,
            name: t.team_name || t.team_key,
            subLabel: t.team_key,
          })),
      });
    } catch (e: any) {
      setTeamsCol({ status: 'error', message: e.message });
    }
  }, [backendFetch]);

  useEffect(() => {
    const query = globalSearchQuery.trim();
    if (!query) {
      setMatchedSearchResults([]);
      setIsSearching(false);
      return undefined;
    }

    setIsSearching(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const [teamsRes, appsRes, svcsRes, nativeSearchRes] = await Promise.all(
          [
            backendFetch(
              'teams',
              `/search?search=${encodeURIComponent(query)}`,
            ).then(r => (r.ok ? r.json() : { teams: [] })),
            backendFetch(
              'applications',
              `/search?search=${encodeURIComponent(query)}`,
            ).then(r => (r.ok ? r.json() : { applications: [] })),
            backendFetch(
              'services',
              `/search?search=${encodeURIComponent(query)}`,
            ).then(r => (r.ok ? r.json() : { services: [] })),
            searchApi.query({ term: query }).catch(() => ({ results: [] })),
          ],
        );

        const teamsList = (teamsRes.teams || []).map((t: any) => ({
          key: t.team_key,
          name: t.team_name || t.team_key,
          type: 'team' as const,
        }));

        const appsList = (appsRes.applications || []).map((a: any) => ({
          key: a.app_key,
          name: a.app_name,
          type: 'application' as const,
        }));

        const svcsList = (svcsRes.services || []).map((s: any) => ({
          key: s.service_key,
          name: s.service_name,
          type: 'service' as const,
        }));

        const nativeList = (nativeSearchRes.results || []).map((res: any) => {
          return {
            key: res.document.location, // Backstage internal navigation path (e.g. "/catalog/default/component/intent-analysis")
            name: res.document.title,
            type: res.type, // Preserves 'software-catalog', 'techdocs', etc.
          };
        });

        setMatchedSearchResults([
          ...teamsList,
          ...appsList,
          ...svcsList,
          ...nativeList,
        ]);
      } catch (error) {
        // Silently handle request errors and clear results
        setMatchedSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300); // 300ms reactive debounce window

    return () => clearTimeout(delayDebounceFn);
  }, [globalSearchQuery, backendFetch, searchApi]);

  const loadApps = useCallback(
    async (filterKeys: Set<string>) => {
      setAppsCol({ status: 'loading' });
      setServicesCol({ status: 'idle' });
      setSelApp(null);
      try {
        const apps = await getApps();
        const filtered = apps
          .filter(a => filterKeys.has(a.team_key))
          .sort((a, b) => a.app_name.localeCompare(b.app_name));
        setAppsCol({
          status: 'ready',
          items: filtered.map(a => ({
            key: a.app_key,
            name: a.app_name,
            subLabel: a.app_key,
          })),
        });
      } catch (e: any) {
        setAppsCol({ status: 'error', message: e.message });
      }
    },
    [getApps],
  );

  const loadServices = useCallback(
    async (appKey: string) => {
      setServicesCol({ status: 'loading' });
      try {
        const services = await getServices();
        const filtered = services
          .filter(s => s.app_key === appKey)
          .sort((a, b) => a.service_name.localeCompare(b.service_name));
        setServicesCol({
          status: 'ready',
          items: filtered.map(s => ({
            key: s.service_key,
            name: s.service_name,
            subLabel: s.service_key,

            serviceKey: s.service_key,
            appKey: s.app_key,
            teamKey: s.team_key,
            serviceType: s.service_type,
            kongEnabled: s.kong_enabled,
            kongPath: s.kong_path,
            autoscaling: s.autoscaling,
            iamRole: s.iam_role,
            environment: 'dev',
          })),
        });
      } catch (e: any) {
        setServicesCol({ status: 'error', message: e.message });
      }
    },
    [getServices],
  );

  // ── Poll pending items until webhook fires and removes them from DB ──────────
  // After a deletion PR is raised, items are marked pending.
  // Every 10s re-fetch to detect when the webhook has deleted the DB record.
  useEffect(() => {
    if (
      pendingApps.size === 0 &&
      pendingServices.size === 0 &&
      pendingTeams.size === 0
    ) {
      return undefined;
    }
    const interval = setInterval(async () => {
      try {
        await fetchPendingDeletionRequests();
        const fetches: Array<Promise<void>> = [];
        if (pendingServices.size > 0) {
          fetches.push(
            (async () => {
              const r = await backendFetch('services', '', {
                headers: { 'Cache-Control': 'no-cache' },
              });
              if (r.ok) {
                const data: { services: Service[] } = await r.json();
                const liveKeys = new Set(data.services.map(s => s.service_key));
                setAllServices(data.services);
                reconcilePending({
                  pendingKeys: pendingServices,
                  liveKeys,
                  setCol: setServicesCol,
                  setPending: setPendingServices,
                });
              }
            })()
          );
        }
        if (pendingApps.size > 0) {
          fetches.push(
            (async () => {
              const r = await backendFetch('applications', '', {
                headers: { 'Cache-Control': 'no-cache' },
              });
              if (r.ok) {
                const data: { applications: Application[] } = await r.json();
                const liveKeys = new Set(data.applications.map(a => a.app_key));
                setAllApps(data.applications);
                reconcilePending({
                  pendingKeys: pendingApps,
                  liveKeys,
                  setCol: setAppsCol,
                  setPending: setPendingApps,
                  onRemoved: key => {
                    if (selApp?.key === key) {
                      setSelApp(null);
                      setServicesCol({ status: 'idle' });
                    }
                  },
                });
              }
            })
            ()
          );
        }
        // Teams require BOTH the okta PR and env0 PR to be merged before the
        // webhook deletes the DB row — so polling /api/teams is sufficient,
        // same pattern as apps/services. The row only disappears once both
        // sides have merged (bitbucketWebhookPlugin enforces this).
        if (pendingTeams.size > 0) {
          fetches.push(
            (async () => {
              const r = await backendFetch('teams', '', {
                headers: { 'Cache-Control': 'no-cache' },
              });
              if (r.ok) {
                const data: { teams: Team[] } = await r.json();
                const liveKeys = new Set(data.teams.map(t => t.team_key));
                reconcilePending({
                  pendingKeys: pendingTeams,
                  liveKeys,
                  setCol: setTeamsCol,
                  setPending: setPendingTeams,
                  onRemoved: key => {
                    if (selTeam?.key === key) {
                      setSelTeam(null);
                      setAppsCol({ status: 'idle' });
                      setSelApp(null);
                      setServicesCol({ status: 'idle' });
                    }
                  },
                });
              }
            })
            ()
          );
        }
        await Promise.all(fetches);
      } catch {
        // Polling errors are silent — retries next interval
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [pendingApps, pendingServices, pendingTeams, backendFetch, selApp, selTeam]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!identity) return;
    if (identity.isPlatLead) {
      loadTeams();
      getApps().catch(() => {});
      getServices().catch(() => {});
    } else if (identity.teamKeys.size > 0) {
      loadApps(identity.teamKeys);
      getServices().catch(() => {});
    } else {
      setAppsCol({ status: 'ready', items: [] });
    }
  }, [identity]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Row click handlers ─────────────────────────────────────────────────────
  const handleTeamClick = useCallback(
    (key: string) => {
      const item =
        teamsCol.status === 'ready'
          ? teamsCol.items.find(t => t.key === key) ?? { key, name: key }
          : { key, name: key };
      setSelTeam(item);
      loadApps(new Set([key]));
    },
    [teamsCol, loadApps],
  );

  const handleAppClick = useCallback(
    (key: string) => {
      if (selApp?.key === key) {
        setSelApp(null);
        setSelService(null);
        setServicesCol({ status: 'idle' });
        return;
      }
      const item =
        appsCol.status === 'ready'
          ? appsCol.items.find(a => a.key === key) ?? { key, name: key }
          : { key, name: key };
      setSelApp(item);
      loadServices(key);
    },
    [appsCol, selApp, loadServices],
  );
 const handleServiceClick = useCallback(
  (key: string) => {
    setSelService(key);
  },
  [],
);
  

  // ── Delete handlers ────────────────────────────────────────────────────────
  const openDeleteModal = useCallback(
    (type: DeleteTarget['type'], key: string, name: string) => {
      if (type === 'service') {
        const appKey = selApp?.key ?? '';

        if (!appKey) {
          // Backend requires app_key on the delete request — without a
          // selected application we cannot build a valid request at all.
          // Surface this immediately rather than letting the DELETE call
          // fail with a 400 after the user has already confirmed.
          setDeleteModal({
            target: { type, key, name, appKey: '' },
            loading: false,
            error:
              'Could not determine the parent application for this service — please reselect it from the Applications column and try again.',
            deleteRepo: false,
          });
          return;
        }

        setDeleteModal({
          target: { type, key, name, appKey },
          loading: false,
          error: null,
          deleteRepo: false,
        });
      } else if (type === 'application') {
        // Compute the real Bitbucket app repo slug from allApps — the
        // "delete repo" checkbox belongs here, not on service deletion,
        // since it's the application's own repo being offered for deletion.
        const app = (allApps ?? []).find(a => a.app_key === key);
        const teamKey = app?.team_key ?? '';
        const appRepoSlug = teamKey ? `tf-ws-eks-${teamKey}-${key}` : '';

        setDeleteModal({
          target: { type, key, name, appRepoSlug },
          loading: false,
          error: null,
          deleteRepo: false,
        });
      } else {
        // type === 'team'
        setDeleteModal({
          target: { type, key, name, teamRepoSlug: `tf-ws-eks-${key}` },
          loading: false,
          error: null,
          deleteRepo: false,
        });
      }
    },
    [allApps, selApp],
  );

  const toggleDeleteRepo = useCallback(() => {
    setDeleteModal(prev =>
      prev ? { ...prev, deleteRepo: !prev.deleteRepo } : null,
    );
  }, []);

  const closeDeleteModal = useCallback(() => setDeleteModal(null), []);

  // ── Teams: dual-PR flow (okta + env0 repos) — both must merge ──────────────
  // alreadyDeleted: no terraform existed anywhere — removed directly.
  // Otherwise: mark pending and show both PR links; the row only disappears
  // once both webhooks have fired (enforced server-side). Extracted from
  // confirmDelete to keep that callback's cognitive complexity down (S3776).
  const applyTeamDeleteResult = useCallback(
    (body: any, target: Extract<DeleteTarget, { type: 'team' }>) => {
      if (body.alreadyDeleted) {
        removeColItem(setTeamsCol, target.key);
        if (selTeam?.key === target.key) {
          setSelTeam(null);
          setAppsCol({ status: 'idle' });
          setSelApp(null);
          setServicesCol({ status: 'idle' });
        }
        fetchPendingDeletionRequests().catch(() => {});
        return;
      }

      setPendingTeams(prev => new Set([...prev, target.key]));
      fetchPendingDeletionRequests().catch(() => {});
      setPrResult({
        targetType: 'team',
        targetKey: target.key,
        targetName: target.name,
        prUrl: null,
        prNumber: null,
        teamPrs: { okta: body.okta, env0: body.env0 },
      });
    },
    [selTeam, fetchPendingDeletionRequests],
  );

  // ── Applications / Services: nothing found in the repo to remove ───────────
  // Without this, alreadyDeleted falls through into the "PR raised" branch,
  // reads an undefined prUrl, and the success modal never opens — the row just
  // vanishes with no explanation. Extracted from confirmDelete (S3776).
  const applyAlreadyDeletedResult = useCallback(
    (target: Exclude<DeleteTarget, { type: 'team' }>) => {
      if (target.type === 'application') {
        removeColItem(setAppsCol, target.key);
        if (selApp?.key === target.key) {
          setSelApp(null);
          setServicesCol({ status: 'idle' });
        }
      } else {
        removeColItem(setServicesCol, target.key);
      }

      fetchPendingDeletionRequests().catch(() => {});

      setPrResult({
        targetType: target.type,
        targetKey: target.key,
        targetName: target.name,
        prUrl: null,
        prNumber: null,
        alreadyDeleted: true,
      });
    },
    [selApp, fetchPendingDeletionRequests],
  );

  // ── Applications / Services: PR raised — mark pending until webhook fires ───
  // DB record is NOT deleted yet; bitbucketWebhookPlugin deletes it when the PR
  // merges and the 10s poll detects it gone. Extracted from confirmDelete
  // (S3776).
  const applyPrRaisedResult = useCallback(
    (body: any, target: Exclude<DeleteTarget, { type: 'team' }>) => {
      const prUrl: string | null = body.prUrl ?? null;
      const prNumber: number | null = body.prNumber ?? null;
      const warning: string | undefined = body.warning;

      fetchPendingDeletionRequests().catch(() => {});

      if (target.type === 'application') {
        setPendingApps(prev => new Set([...prev, target.key]));
      } else {
        setPendingServices(
          prev =>
            new Set([...prev, servicePendingKey(target.appKey, target.key)]),
        );
      }

      // Show PR link modal so user knows to review and merge it
      if (prUrl) {
        setPrResult({
          targetType: target.type,
          targetKey: target.key,
          targetName: target.name,
          prUrl,
          prNumber,
          warning,
          repoToDelete: body.repoToDelete ?? null,
        });
      }
    },
    [fetchPendingDeletionRequests],
  );

  const confirmDelete = useCallback(async () => {
    if (!deleteModal) return;
    const { target } = deleteModal;
    setDeleteModal(prev =>
      prev ? { ...prev, loading: true, error: null } : null,
    );

    const { pluginId, path } = resolveDeleteEndpoint(
      target,
      deleteModal.deleteRepo,
    );

    try {
      const r = await backendFetch(pluginId, path, {
        method: 'DELETE',
      });
      const body = await r.json();

      if (!r.ok) {
        setDeleteModal(prev =>
          prev
            ? {
                ...prev,
                loading: false,
                error: body.error ?? `HTTP ${r.status}`,
              }
            : null,
        );
        return;
      }

      closeDeleteModal();

      if (target.type === 'team') {
        applyTeamDeleteResult(body, target);
        return;
      }

      if (body.alreadyDeleted) {
        applyAlreadyDeletedResult(target);
        return;
      }

      applyPrRaisedResult(body, target);
    } catch (e: any) {
      setDeleteModal(prev =>
        prev ? { ...prev, loading: false, error: e.message } : null,
      );
    }
  }, [
    deleteModal,
    backendFetch,
    closeDeleteModal,
    applyTeamDeleteResult,
    applyAlreadyDeletedResult,
    applyPrRaisedResult,
  ]);

  // Load the applications column for a team, sorted by name. Shared by the
  // global-search "jump to application" path.
  const populateAppColumn = useCallback(
    (teamKey: string) => {
      setAppsCol({ status: 'loading' });
      getApps()
        .then(apps => {
          const items = apps
            .filter(a => a.team_key === teamKey)
            .sort((a, b) => a.app_name.localeCompare(b.app_name))
            .map(a => ({ key: a.app_key, name: a.app_name, subLabel: a.app_key }));
          setAppsCol({ status: 'ready', items });
        })
        .catch(() => {
          setAppsCol({
            status: 'error',
            message: 'Failed to populate column path',
          });
        });
    },
    [getApps],
  );

  // Navigate the three columns (or the router) to a chosen global-search hit.
  // Extracted from the results dropdown JSX so that onClick isn't a five-deep
  // nested arrow (S2004).
  const handleSearchResultClick = useCallback(
    (result: { key: string; name: string; type: string }) => {
      setGlobalSearchQuery('');

      if (result.type === 'team') {
        setSelTeam({ key: result.key, name: result.name });
        loadApps(new Set([result.key]));
        setSelApp(null);
        setServicesCol({ status: 'idle' });
        return;
      }

      if (result.type === 'application') {
        const app = (allApps ?? []).find(a => a.app_key === result.key);
        if (!app) return;
        setSelTeam({ key: app.team_key, name: app.team_key });
        populateAppColumn(app.team_key);
        setSelApp({ key: app.app_key, name: app.app_name });
        loadServices(app.app_key);
        return;
      }

      if (result.type === 'service') {
        navigate(`/catalog/default/component/${result.key}`);
        return;
      }

      // Catalog entity / TechDocs location — the key is already a route.
      navigate(result.key);
    },
    [allApps, loadApps, loadServices, navigate, populateAppColumn],
  );

  // ── Loading gate ───────────────────────────────────────────────────────────
  if (!identity) {
    return (
      <div style={S.page}>
        <SpinCSS />
        <div style={S.initOverlay}>
          <div style={S.spinEl} />
          <span>Resolving identity…</span>
        </div>
      </div>
    );
  }

  // ── Role-based delete visibility ───────────────────────────────────────────
  //
  //  Teams:        plat-lead only (direct delete — no PR)
  //  Applications: plat-lead OR any team-lead (via PR)
  //  Services:     plat-lead OR any team-lead OR any team-dev (via PR)
  //
  const canDeleteTeams = identity.isPlatLead;
  const canDeleteApps = identity.isAnyLead;
  const canDeleteServices = identity.isAnyLead || identity.isAnyDev;

  // ── Badge & breadcrumbs (computed by module helpers to keep this
  //    component's cognitive complexity down — S3776) ─────────────────────────
  const badge = roleBadge(identity);
  const appsBc = appsBreadcrumb(identity, selTeam);
  const servicesBc = servicesBreadcrumb(selTeam, selApp);
  const serviceAppKey = selApp?.key;

  return (
    <div style={S.page}>
      <SpinCSS />

      <div style={S.header}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}
        >
          <h1 style={S.headerTitle}>DT Self Service - Devex Portal</h1>

          {/* Global Search Component placed directly next to the title */}
          <div style={S.globalSearchWrapper}>
            <span style={S.globalSearchIcon}>
              {isSearching ? (
                // Display visual searching spin state
                <div
                  style={{
                    ...S.spinEl,
                    width: 14,
                    height: 14,
                    margin: 0,
                    borderWidth: '1.5px',
                  }}
                />
              ) : (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              )}
            </span>
            <input
              type="text"
              placeholder="Search teams, apps, services..."
              value={globalSearchQuery}
              onChange={e => setGlobalSearchQuery(e.target.value)}
              onFocus={() => setGlobalSearchFocused(true)}
              onBlur={() =>
                setTimeout(() => setGlobalSearchFocused(false), 200)
              } // delay to allow clicks
              style={{
                ...S.globalSearchInput,
                ...(globalSearchFocused ? S.searchInputActive : {}),
              }}
            />
            {globalSearchQuery && (
              <button
                type="button"
                onClick={() => setGlobalSearchQuery('')}
                style={S.globalSearchClearBtn}
                title="Clear search"
              >
                ✕
              </button>
            )}

            {/* Dropdown displaying results below the search input */}
            {globalSearchFocused && matchedSearchResults.length > 0 && (
              <div style={S.globalSearchDropdown}>
                {matchedSearchResults.map((result, idx) => (
                  <button
                    key={`${result.type}-${result.key}`}
                    type="button"
                    onMouseEnter={() => setHoveredSearchIndex(idx)}
                    onMouseLeave={() => setHoveredSearchIndex(null)}
                    onClick={() => handleSearchResultClick(result)}
                    style={{
                      ...S.globalSearchItem,
                      ...(hoveredSearchIndex === idx
                        ? S.globalSearchItemHover
                        : {}),
                    }}
                  >
                    <div style={S.globalSearchItemMeta}>
                      <span style={S.globalSearchItemName}>{result.name}</span>
                      <span style={S.globalSearchItemType}>{result.type}</span>
                    </div>
                    <span style={S.globalSearchItemKey}>{result.key}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <span style={S.badge}>{badge}</span>
      </div>

      <div style={S.cols}>
        {identity.isPlatLead && (
          <Column
            title="Teams"
            state={teamsCol}
            selected={selTeam?.key ?? null}

            onSelect={handleTeamClick}
            onDelete={(key, name) => openDeleteModal('team', key, name)}
            canDelete={canDeleteTeams}
            pendingKeys={pendingTeams}
            pendingUrls={pendingUrls}
            idleLabel="No teams available"
            emptyLabel="No teams found"
          />
        )}

        <Column
          title="Applications"
          state={appsCol}
          selected={selApp?.key ?? null}
          onSelect={handleAppClick}
          onDelete={(key, name) => openDeleteModal('application', key, name)}
          canDelete={canDeleteApps}
          pendingKeys={pendingApps}
          pendingUrls={pendingUrls}
          breadcrumb={appsBc}
          idleLabel={
            identity.isPlatLead ? '← Select a team' : 'Loading applications…'
          }
          emptyLabel="No applications found for this team"
          renderDetail={item => {
            const app = allApps?.find(a => a.app_key === item.key);
            return app ? <AppDetailPanel app={app} /> : null;
          }}
        />

        <Column
          title="Services"
          state={servicesCol}
          selected={selService}
          onSelect={handleServiceClick}
          onDelete={(key, name) => openDeleteModal('service', key, name)}
          canDelete={canDeleteServices}
          pendingKeys={pendingServices}
          pendingUrls={pendingUrls}
          breadcrumb={servicesBc}
          idleLabel="← Select an application"
          emptyLabel="No services found for this application"
          isLast
          showChevron={serviceAppKey !== undefined}
          getPendingKey={key =>
            serviceAppKey === undefined ? key : servicePendingKey(serviceAppKey, key)
          }
          renderDetail={item =>
            serviceAppKey ? (
              <ServiceDetailPanel
                serviceKey={item.key}
                serviceName={item.name}
                appKey={serviceAppKey}
              />
            ) : null
          }
        />
      </div>

      {deleteModal && (
        <DeleteModal
          state={deleteModal}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          onToggleRepo={toggleDeleteRepo}
        />
      )}

      {prResult && (
        <PrSuccessModal result={prResult} onClose={() => setPrResult(null)} />
      )}

      {/* Floating Action Buttons Container */}
      <div
        style={{
          position: 'absolute',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          display: 'flex',
          gap: 12,
        }}
      >
        {identity.isPlatLead && (
          <button
            type="button"
            style={{
              ...S.feedbackBtn,
              position: 'static',
              background: '#ffffff',
              color: '#1967d2',
              border: '1px solid #1967d2',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
              ...(isAdminFeedbackBtnHovered
                ? {
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 20px rgba(25, 103, 210, 0.2)',
                  }
                : {}),
            }}
            onClick={() => setIsAdminFeedbackOpen(true)}
            onMouseEnter={() => setIsAdminFeedbackBtnHovered(true)}
            onMouseLeave={() => setIsAdminFeedbackBtnHovered(false)}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            View Feedback
          </button>
        )}

        <button
          type="button"
          style={{
            ...S.feedbackBtn,
            position: 'static',
            ...(isFeedbackBtnHovered
              ? {
                  transform: 'scale(1.05)',
                  boxShadow: '0 6px 20px rgba(132, 199, 252, 0.6)',
                }
              : {}),
          }}
          onClick={() => setIsFeedbackOpen(true)}
          onMouseEnter={() => setIsFeedbackBtnHovered(true)}
          onMouseLeave={() => setIsFeedbackBtnHovered(false)}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          Feedback
        </button>
      </div>

      {/* Feedback Modal Overlay */}
      {isFeedbackOpen && (
        <div
          style={S.modalOverlay}
          onClick={e => e.target === e.currentTarget && closeFeedbackModal()}
          onKeyDown={e => e.key === 'Escape' && closeFeedbackModal()}
          role="presentation"
        >
          <div style={S.modalCard}>
            <h2 style={S.modalTitle}>Share your Feedback</h2>

            {feedbackSuccess ? (
              <div style={S.successMsg}>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2e7d32"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginBottom: 12 }}
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <div>Thank you for your feedback!</div>
              </div>
            ) : (
              <form
                onSubmit={handleFeedbackSubmit}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <div style={S.starsRow}>
                  {[1, 2, 3, 4, 5].map(star => {
                    const active =
                      star <= (feedbackHoverRating || feedbackRating);
                    return (
                      <button
                        key={star}
                        type="button"
                        style={{
                          ...S.starBtn,
                          transform:
                            star <= feedbackHoverRating
                              ? 'scale(1.15)'
                              : 'scale(1)',
                        }}
                        onClick={() => setFeedbackRating(star)}
                        onMouseEnter={() => setFeedbackHoverRating(star)}
                        onMouseLeave={() => setFeedbackHoverRating(0)}
                      >
                        <svg
                          width="36"
                          height="36"
                          viewBox="0 0 24 24"
                          fill={active ? '#FFB300' : 'none'}
                          stroke={active ? '#FFB300' : '#888888'}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ transition: 'fill 0.1s, stroke 0.1s' }}
                        >
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </button>
                    );
                  })}
                </div>

                <textarea
                  style={{ ...S.commentsArea, marginBottom: 8 }}
                  placeholder="Tell us about your experience..."
                  value={feedbackComments}
                  onChange={e => setFeedbackComments(e.target.value)}
                  maxLength={2000}
                  disabled={isSubmittingFeedback}
                />
                <div
                  style={{
                    alignSelf: 'flex-start',
                    fontSize: 11,
                    color: '#666666',
                    marginBottom: 20,
                  }}
                >
                  {2000 - feedbackComments.length} characters remaining
                </div>

                <div style={{ alignSelf: 'flex-start', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 14, color: '#333333', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={identifyMe}
                      onChange={e => setIdentifyMe(e.target.checked)}
                      disabled={isSubmittingFeedback}
                      style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#1967d2' }}
                    />
                    Identify me with this feedback
                  </label>
                  <span style={{ fontSize: 11, color: '#666666', marginLeft: 24 }}>
                    Your identity will be visible to platform leads. Otherwise, feedback is anonymous.
                  </span>
                </div>

                {feedbackError && (
                  <div style={{ ...S.errorMsg, marginBottom: 16 }}>
                    {feedbackError}
                  </div>
                )}

                <div style={S.modalActions}>
                  <button
                    type="button"
                    style={S.cancelBtn}
                    onClick={closeFeedbackModal}
                    disabled={isSubmittingFeedback}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      ...S.submitBtn,
                      opacity: isSubmittingFeedback ? 0.7 : 1,
                      cursor: isSubmittingFeedback ? 'not-allowed' : 'pointer',
                    }}
                    disabled={isSubmittingFeedback}
                  >
                    {isSubmittingFeedback ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Admin Feedback Log Modal */}
      {isAdminFeedbackOpen && (
        <div
          style={S.modalOverlay}
          onClick={e =>
            e.target === e.currentTarget && setIsAdminFeedbackOpen(false)
          }
          onKeyDown={e => e.key === 'Escape' && setIsAdminFeedbackOpen(false)}
          role="presentation"
        >
          <div style={S.adminModalCard}>
            <h2 style={S.modalTitle}>User Feedback Log</h2>

            {renderAdminFeedbackContent()}

            <div style={S.modalActions}>
              <button
                type="button"
                style={S.cancelBtn}
                onClick={() => setIsAdminFeedbackOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Standalone AuditLogPage Component ─────────────────────────────────────────

export const AuditLogPage = () => {
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  // See backendFetch in DashboardPage above for why this explicit resolution
  // + bearer token attachment is needed once frontend/backend are split.
  const backendFetch = useCallback(
    async (pluginId: string, path: string, init?: RequestInit) => {
      const baseUrl = await discoveryApi.getBaseUrl(pluginId);
      const { token } = await identityApi.getCredentials();
      return fetchApi.fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    },
    [discoveryApi, fetchApi, identityApi],
  );

  const [auditEntityType, setAuditEntityType] = useState<string>('all');
  const [auditEntityKey, setAuditEntityKey] = useState<string>('');
  const [auditActor, setAuditActor] = useState<string>('');
  const [auditAction, setAuditAction] = useState<string>('all');
  const [auditStartDate, setAuditStartDate] = useState<string>('');
  const [auditEndDate, setAuditEndDate] = useState<string>('');
  const [auditPage, setAuditPage] = useState<number>(0);
  const auditLimit = 25;

  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditTotal, setAuditTotal] = useState<number>(0);
  const [auditLoading, setAuditLoading] = useState<boolean>(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [selectedAuditLog, setSelectedAuditLog] = useState<any | null>(null);

  const fetchAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const params = new URLSearchParams();
      if (auditEntityType && auditEntityType !== 'all') params.append('entityType', auditEntityType);
      if (auditEntityKey.trim()) params.append('entityKey', auditEntityKey.trim());
      if (auditActor.trim()) params.append('actor', auditActor.trim());
      if (auditAction && auditAction !== 'all') params.append('action', auditAction);
      if (auditStartDate) params.append('startDate', auditStartDate);
      if (auditEndDate) params.append('endDate', auditEndDate);
      params.append('limit', String(auditLimit));
      params.append('offset', String(auditPage * auditLimit));

      const res = await backendFetch('audit-log', `?${params.toString()}`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      const data = await res.json();
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
    } catch (err: any) {
      setAuditError(err.message || 'Failed to fetch audit logs');
    } finally {
      setAuditLoading(false);
    }
  }, [backendFetch, auditEntityType, auditEntityKey, auditActor, auditAction, auditStartDate, auditEndDate, auditPage]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const [identity, setIdentity] = useState<{
    isPlatLead: boolean;
    isAnyLead: boolean;
    isAnyDev: boolean;
    teamKeys: Set<string>;
  } | null>(null);

  useEffect(() => {
    identityApi.getBackstageIdentity().then(id => {
      setIdentity(resolveDashboardIdentity(id.ownershipEntityRefs));
    });
  }, [identityApi]);

  if (!identity) {
    return (
      <div style={S.page}>
        <SpinCSS />
        <div style={S.initOverlay}>
          <div style={S.spinEl} />
          <span>Resolving identity…</span>
        </div>
      </div>
    );
  }

  let badge = 'no team membership detected';
  if (identity.isPlatLead)
    badge = 'eks-team-plat-lead · sees all teams, apps, services';
  else if (identity.teamKeys.size > 0)
    badge = `sees ${[...identity.teamKeys].join(' + ')} only`;

  return (
    <div style={S.page}>
      <SpinCSS />
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
          <h1 style={S.headerTitle}>DT Self Service - Audit Log</h1>
        </div>
        <span style={S.badge}>{badge}</span>
      </div>

      <AuditLogView
        logs={auditLogs}
        total={auditTotal}
        loading={auditLoading}
        error={auditError}
        entityType={auditEntityType}
        setEntityType={setAuditEntityType}
        entityKey={auditEntityKey}
        setEntityKey={setAuditEntityKey}
        actor={auditActor}
        setActor={setAuditActor}
        action={auditAction}
        setAction={setAuditAction}
        startDate={auditStartDate}
        setStartDate={setAuditStartDate}
        endDate={auditEndDate}
        setEndDate={setAuditEndDate}
        page={auditPage}
        setPage={setAuditPage}
        limit={auditLimit}
        onRefresh={fetchAuditLogs}
        onSelectRecord={setSelectedAuditLog}
        backendFetch={backendFetch}
      />

      {/* Inspect JSON Modal */}
      {selectedAuditLog && (
        <div
          style={S.modalOverlay}
          onClick={e => e.target === e.currentTarget && setSelectedAuditLog(null)}
          onKeyDown={e => e.key === 'Escape' && setSelectedAuditLog(null)}
          role="presentation"
        >
          <div style={{ background: '#ffffff', borderRadius: 12, width: 600, maxWidth: '90vw', padding: 24, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#111111' }}>
              Audit Log Detail #{selectedAuditLog.id}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              <div><strong>Entity Type:</strong> {selectedAuditLog.entity_type}</div>
              <div><strong>Entity Key:</strong> {selectedAuditLog.entity_key}</div>
              <div><strong>Action:</strong> {selectedAuditLog.action}</div>
              <div><strong>Performed By:</strong> {formatActor(selectedAuditLog.actor)}</div>
              <div><strong>Status:</strong> {selectedAuditLog.status}</div>
              <div><strong>Timestamp:</strong> {new Date(selectedAuditLog.timestamp).toLocaleString()}</div>
            </div>

            {selectedAuditLog.error && (
              <div style={{ background: '#fce8e6', color: '#c5221f', padding: 12, borderRadius: 6, fontSize: 13, border: '1px solid #f9d2ce' }}>
                <strong>Error:</strong> {selectedAuditLog.error}
              </div>
            )}

            <div>
              <strong style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Raw Event Metadata:</strong>
              <pre style={{ background: '#282c34', color: '#abb2bf', padding: 14, borderRadius: 8, fontSize: 12, overflowX: 'auto', margin: 0, maxHeight: 220 }}>
                {JSON.stringify(selectedAuditLog.metadata ?? {}, null, 2)}
              </pre>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setSelectedAuditLog(null)}
                style={{ padding: '8px 16px', background: '#1967d2', color: '#ffffff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: 'pointer', fontSize: 13 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};